// TODO: 修正必要

/** @jsxRuntime classic */
/** @jsx jsx */
import { Global, css, jsx } from "@emotion/react";
import InfoIcon from "@material-ui/icons/Info";
import VolumeOffIcon from "@material-ui/icons/VolumeOff";
import React, { useCallback, useEffect, useState } from "react";
import throttle from "lodash.throttle";
import { EventInfo } from "utility";

import { ToggleSwitchHandleChange } from "components/switch/ReactSwitch";
import { useI18n } from "hooks/i18n/i18n";
import { IVSVideoPlayer, PlainVideoPlayer } from "./videojsPlayer";
import { THEOPLAYER_BASE_STYLE } from "./styles/theoplayerBaseStyle";
import { useTheoplayerContainer } from "./hooks/useTheoplayer";

type StreamingScreenProps = {
  THEOPlayerElementHeight: number;
  player: PlainVideoPlayer | null;
  awsPlayer: IVSVideoPlayer | null;
  eventInfo: EventInfo;
  useLL: boolean;
  hasURL: boolean;
  isControllerRendering: boolean;
  isCancelLowLatencyMessage: boolean;
  setLLUrl: ToggleSwitchHandleChange;
  setTHEOPlayerElementHeight: (THEOPlayerElementHeight: number) => void;
  setIsControllerRendering: () => void;
  setVideoRef: (videoRef: HTMLVideoElement | null) => void;
  setIVSVideoRef: (videoRef: HTMLVideoElement | null) => void;
};
const StreamingScreenV2: React.FC<StreamingScreenProps> = (props) => {
  const containerRef = useTheoplayerContainer();
  const { t } = useI18n();

  const [isMuted, setIsMuted] = useState<boolean>(false);
  const unmute = () => {
    if (!!props.awsPlayer && !props.awsPlayer.paused()) {
      props.awsPlayer.setMuted(false);
      props.awsPlayer.setVolume(1.0);
    }
    if (!!props.player && !props.player.paused()) {
      props.player.setMuted(false);
    }
    setIsMuted(false);
  };

  const syncIsMuted = useCallback(
    (player: PlainVideoPlayer | IVSVideoPlayer | null) => {
      const updatePlayerState = throttle(
        () => player && setIsMuted(player.muted()),
        100
      );

      if (player) {
        player.addEventListener("play", updatePlayerState);
        player.addEventListener("pause", updatePlayerState);
        player.addEventListener("timeupdate", updatePlayerState);
        player.addEventListener("sourcechange", updatePlayerState);
        player.addEventListener("volumechange", updatePlayerState);
      }
      return () => {
        if (player) {
          player.removeEventListener("play", updatePlayerState);
          player.removeEventListener("pause", updatePlayerState);
          player.removeEventListener("timeupdate", updatePlayerState);
          player.removeEventListener("sourcechange", updatePlayerState);
          player.removeEventListener("volumechange", updatePlayerState);
        }
      };
    },
    [setIsMuted]
  );
  useEffect(() => syncIsMuted(props.player), [props.player, syncIsMuted]);
  useEffect(() => syncIsMuted(props.awsPlayer), [props.awsPlayer, syncIsMuted]);

  return (
    <div id="video">
      <Global styles={THEOPLAYER_BASE_STYLE} />
      <Global styles={THEOPlayerStyle} />

      {isMuted && (
        <div css={unmuteButtonStyle} onClick={unmute}>
          <VolumeOffIcon />
          {t("streaming.controller.mute")}
        </div>
      )}

      {/* 低遅延モードが無効になりました。を表示する。 */}
      <div
        css={changeLowLatencyStyle}
        className={props.isCancelLowLatencyMessage ? `` : `hide`}
      >
        <InfoIcon />
        {t("streaming.mode.LLDisabled")}
      </div>

      <div ref={containerRef} id="theoplayer-container"></div>

      <div data-vjs-player>
        <video
          ref={props.setIVSVideoRef}
          style={{ display: "none" }}
          id="aws-video-player"
          className="video-js"
          controls
          playsInline
          poster={props.eventInfo.thumbnail}
        ></video>
      </div>
    </div>
  );
};

export default StreamingScreenV2;

/**
 * @description
 * theoplayerのデフォルトスタイルを上書きする
 * これらのスタイルはtheoplayerがdefaultで提供しているものであり、今回のデザインには不要なものは非表示化する。
 * もし将来的に必要になった場合は、ここから削除して再度表示させることができる。
 */
const overwriteTheoPlayerStyle = css`
  /* primary colorを白色に上書きする */
  .theo-primary-color {
    color: #ffffff !important;
  }
  .theoplayer-skin .theo-settings-control-label-hd {
    color: #5e5e5e;
    background-color: #ffffff;
  }
  .theo-primary-background.theo-menu-header {
    color: #ffffff !important;
    background-color: rgba(0, 0, 0, 0.6) !important;
  }
  .theo-submenu .theo-primary-background.theo-menu-header {
    background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTcuNzcgMy43N0wxNiAyTDYgMTJMMTYgMjJMMTcuNzcgMjAuMjNMOS41NCAxMkwxNy43NyAzLjc3WiIgZmlsbD0id2hpdGUiLz48L3N2Zz4=") !important;
    background-repeat: no-repeat !important;
    background-position: 6px center !important;
    background-size: 14px !important;
  }
  .theoplayer-skin .theo-menu .theo-menu-item:focus,
  .theoplayer-skin .theo-menu .theo-menu-item:hover {
    color: #ffffff !important;
    background-color: rgba(255, 255, 255, 0.2) !important;
  }
  .theoplayer-skin .theo-menu .theo-menu-item.vjs-selected {
    color: #000000;
    background-color: #dcdcdc;
  }
  .theoplayer-skin .theo-live-control-text {
    margin-left: 4px;
  }
  /* theoplayerがdefaultで提供しているfullscreenを有効にするbuttonを非表示化する */
  .vjs-fullscreen-control {
    display: none !important;
  }
  /* theoplayerがdefaultで提供している大きい再生buttonを非表示化する */
  .vjs-big-play-button {
    display: none !important;
  }
  /* theoplayerがdefaultで提供している大きい再生buttonを非表示化する */
  .theoplayer-skin .vjs-big-play-button:after {
    content: none !important;
  }
  /* theoplayerがdefaultで提供しているcontrol barの影を非表示化する */
  .theoplayer-skin .theo-control-bar-shadow {
    background: none;
  }
  .theoplayer-skin .vjs-control-bar .vjs-control.vjs-button {
    width: 30px;
    height: 28px;
  }
  .theoplayer-skin:not(.theo-seekbar-inside-controls) {
    .vjs-custom-control-spacer {
      display: none;
    }
  }
  .theo-text-track-control-button {
    display: none !important;
  }
  .theo-audio-track-control-button {
    display: none !important;
  }
  .theoplayer-skin.theo-mobile:not(.theo-seekbar-inside-controls) {
    .vjs-progress-control {
      padding-top: 0 !important;
      padding-bottom: 0 !important;
      margin-bottom: 0 !important;
    }
  }
  .theoplayer-skin.vjs-fullscreen {
    .vjs-control-bar {
      height: auto !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }
  .theoplayer-skin.theo-mobile.video-js .vjs-control:before {
    line-height: 1 !important;
  }
`;

/**
 * @description
 * スマホ表示の時に画面の中心に表示されるモバイル用のコントローラーのスタイル
 */
const mobileControllerStyle = css`
  .video-js {
    .vjs-custom-mobile-controller {
      position: absolute;
      top: 50%;
      left: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      grid-gap: 40px;
      transform: translate(-50%, -50%);
      transition:
        visibility 0.5s,
        opacity 0.3s;
      z-index: 100;
      .vjs-custom-mobile-controller-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 56px;
        height: 56px;
        padding: 15px;
        background-color: #00000080;
        border-radius: 50%;
        cursor: pointer;
        svg {
          width: 26px;
          height: 26px;
        }
      }
    }
  }
`;

/**
 * @description
 * プレイヤーのボタンの順番を変更する
 */
const changeButtonOrder = css`
  .vjs-custom-rewind-button {
    order: -100;
  }
  .vjs-custom-forward-button {
    order: -99;
  }
  /* 設定ボタンの右に出るように順番を変更する */
  .vjs-custom-theater-mode-button {
    order: 96;
  }
  .vjs-custom-fullscreen-comment-button {
    order: 97;
  }
  .vjs-custom-fullscreen-button,
  .vjs-custom-fullscreen-mode-button {
    order: 98;
  }
`;

const settingMenuStyle = css`
  .vjs-menu-button-popup .vjs-menu.vjs-lock-showing {
    min-width: 18em;
    left: auto;
    right: -100%;
    bottom: 16px;
  }
  &.vjs-layout-small,
  &.vjs-layout-x-small {
    .vjs-menu-button-popup .vjs-menu.vjs-lock-showing {
      bottom: 0;
    }
  }
  /* シアターモードボタンが無い場合はその分右に寄せる */
  /* prettier-ignore */
  .vjs-control-bar:not(:has(.vjs-custom-theater-mode-button))
  .vjs-menu-button-popup
  .vjs-menu.vjs-lock-showing {
    right: -350%;
  }
  .vjs-menu-button.vjs-hover .vjs-menu:not(.vjs-lock-showing) {
    visibility: hidden;
  }
  .vjs-menu {
    /* 見出しを常に#000000にする */
    .vjs-menu-content {
      .vjs-menu-item:first-of-type {
        background-color: rgba(0, 0, 0, 1) !important;
        &:hover {
          background-color: rgba(0, 0, 0, 1) !important;
        }
      }
    }
    .vjs-settings-menu-header {
      padding: 0.6em 1em;
      color: #ffffff !important;
      line-height: 1.4em;
      opacity: 1;
      & > .vjs-menu-item-text {
        justify-content: center !important;
      }
    }
    .vjs-menu-item {
      align-items: center !important;
      padding: 0.6em 1em;
      color: #ffffff !important;
      background-color: rgba(0, 0, 0, 0.5) !important;
      &:hover {
        background-color: rgba(255, 255, 255, 0.2) !important;
      }
      .vjs-menu-item-text {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 14px;
        font-weight: bold;
      }
      .theo-settings-control-menu-item-value {
        display: flex;
        align-items: baseline;
        gap: 4px;
      }
    }
    &.vjs-settings-menu-quality {
      .vjs-menu-item:not(:first-of-type) {
        .vjs-menu-item-text {
          justify-content: center !important;
          font-weight: normal !important;
        }
      }
    }
  }
`;

/**
 * CSS-in-JS
 */
const THEOPlayerStyle = css`
  ${overwriteTheoPlayerStyle}
  ${mobileControllerStyle}
  ${settingMenuStyle}
  ${changeButtonOrder}
  .aws-video-player {
    display: block;
    width: 100%;
  }
  /* lowercaseやuppercaseになっているものを全て、入力値通りに上書きする */
  .vjs-menu * {
    text-transform: none !important;
  }
  .vjs-live {
    .theo-chromecast-button,
    .theo-airplay-button {
      display: none !important;
    }
  }
  .theo-at-live {
    .theo-chromecast-button,
    .theo-airplay-button {
      display: none !important;
    }
  }

  /**
   * @todo 今回追加したシアターモードボタンに関係するCSSは別のプルリクで、registerの処理で管理するようにする。
   */
  .fullscreen-mode-enabled {
    .vjs-custom-theater-mode-button {
      display: none;
    }
  }
  .video-js {
    /* プレイヤーのコントロールバーのスタイルを上書きする */
    .vjs-custom-overlay-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      padding: 20px;
      background-color: #00000080;
      border-radius: 50%;
      transform: translate(-50%, -50%);
      transition:
        visibility 0.3s,
        opacity 0.3s;
      z-index: 100;
      pointer-events: none;
      svg {
        width: 32px;
        height: 32px;
      }
    }
    .vjs-custom-overlay-play-icon {
      opacity: 0;
      visibility: hidden;
      &.active {
        opacity: 1;
        visibility: visible;
      }
    }
    .vjs-custom-overlay-pause-icon {
      opacity: 0;
      visibility: hidden;
      &.active {
        opacity: 1;
        visibility: visible;
      }
    }

    /* プレイヤーのコントロールバーのスタイルを上書きする */
    .vjs-control-bar {
      display: flex;
      position: absolute;
      left: 0;
      right: 0;
      bottom: 8px;
      margin: auto;
      align-items: center;
      width: calc(100% - 16px);
      height: auto;
      padding: 28px 16px 6px;
      border-radius: 12px;
      z-index: 1000;

      /* RTX VSR が非アクティブになるため、backdrop-filter は使用しない。できるだけ見た目を近づけている */
      background-color: rgba(0, 0, 0, 0.5);
      background-image: linear-gradient(
        to bottom,
        rgba(255, 255, 255, 0.06),
        rgba(255, 255, 255, 0) 35%,
        rgba(0, 0, 0, 0.1)
      );
      border: 1px solid rgba(255, 255, 255, 0.07);
      box-shadow:
        0 10px 24px rgba(0, 0, 0, 0.35),
        inset 0 1px 0 rgba(255, 255, 255, 0.08);
    }

    /* プログレスバーのスタイルを上書きする */
    .vjs-progress-control {
      position: absolute;
      left: 0;
      right: 0;
      top: 17px;
      margin: auto;
      display: flex !important;
      width: calc(100% - 16px * 2);
      height: 5px;
      .vjs-progress-holder {
        height: 0.4em !important;
        margin: 0 !important;
      }
      /* プログレスバー上に表示される丸のスタイルを上書きする */
      .vjs-play-progress {
        &::before {
          top: 50%;
          right: -0.5em !important;
          transform: translateY(-50%) !important;
          font-size: 0px !important;
          transition: font-size 0.2s;
        }
      }
      &:hover {
        .vjs-progress-holder {
          height: 0.6em !important;
        }
        .vjs-play-progress {
          &::before {
            font-size: 15px !important;
          }
        }
      }
    }

    /**
     * コントロールバーのボタンのスタイルを上書きする。
      ここでは、主にボタンの大きさや位置、アイコンのサイズを調整している。
     */
    .vjs-custom-button {
      cursor: pointer;
      svg {
        width: 25px;
        height: 25px;
      }
    }
    .vjs-menu-button {
      &:before {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 22px !important;
      }
      &:hover:before {
        text-shadow: none;
      }
    }
    .vjs-mute-control {
      &::before {
        /* 再生ボタンより若干大きいので微調整している */
        font-size: 24px !important;
      }
    }
    .vjs-menu {
      z-index: 10000 !important;
    }
    /* コントロールバーの中間の余白を設定する */
    .vjs-custom-spacing {
      flex: 1;
    }
    .vjs-control::before {
      position: static;
      font-size: 26px;
      line-height: 1;
    }
    .vjs-descriptions-button,
    .vjs-audio-track-button,
    .vjs-caption-settings,
    .vjs-subs-caps-button {
      display: none !important;
    }

    /* コントロールバーの再生時間のスタイルを上書きする */
    .vjs-current-time {
      display: block;
      padding: 0px;
      padding-left: 4px;
    }
    .vjs-time-divider {
      display: block;
      padding-right: 2px;
      padding-left: 2px;
      min-width: 0px;
    }
    .vjs-duration {
      display: block;
      padding: 0px;
      padding-right: 4px;
    }
    &.vjs-live {
      .vjs-custom-rewind-button,
      .vjs-custom-forward-button {
        display: none;
      }
    }

    /* 配信の再生時間のスタイルを上書きする */
    .vjs-time-tooltip {
      width: max-content !important;
    }
    &.vjs-has-started {
      .vjs-cast-menu {
        display: block;
      }
    }

    /* 配信を再生中の時は、要素を非表示にする */
    &.vjs-has-started.vjs-user-inactive.vjs-playing {
      .vjs-cast-menu,
      .vjs-custom-mobile-controller {
        opacity: 0;
        visibility: hidden;
        transition:
          visibility 0.1s,
          opacity 0.1s;
      }
    }

    /* 配信を再生中の時は、要素を非表示にする */
    &.vjs-has-started.vjs-user-inactive.vjs-paused {
      .vjs-cast-menu,
      .vjs-custom-mobile-controller {
        display: flex;
        opacity: 1;
        visibility: visible;
        transition:
          visibility 0.1s,
          opacity 0.1s;
      }
    }

    /* プレイヤーのサイズでボタンの表示・非表示を切り替える
     900px 以上 - PC・大型タブレット横 */
    &.vjs-layout-large,
    &.vjs-layout-x-large {
      .vjs-sp-playback-quality,
      .vjs-custom-mobile-controller {
        display: none;
      }
    }

    /* 600px 以上 ～ 900px 未満 - タブレット縦・小型横 */
    &.vjs-layout-medium {
      .vjs-sp-playback-quality,
      .vjs-custom-mobile-controller {
        display: none;
      }
      .vjs-custom-theater-mode-button {
        display: none !important;
        @media screen and (min-width: 768px) {
          display: block !important;
        }
      }
    }

    /* 400px 以上 ～ 600px 未満 - スマホ横・小型タブレット */
    &.vjs-layout-small {
      .vjs-custom-rewind-button,
      .vjs-custom-forward-button,
      .theo-button-tooltip,
      .vjs-time-tooltip,
      .vjs-custom-overlay-play-icon,
      .vjs-custom-overlay-pause-icon {
        display: none;
      }
      .vjs-play-control {
        display: none !important;
      }
      .vjs-custom-theater-mode-button {
        display: none !important;
        @media screen and (min-width: 768px) {
          display: block !important;
        }
      }
      .vjs-control-bar {
        bottom: 0;
        width: 100%;
        padding: 16px 16px 32px;
        backdrop-filter: blur(0px);
        background: linear-gradient(
          0deg,
          rgba(0, 0, 0, 0.6) 0%,
          rgba(255, 255, 255, 0) 100%
        );
        border-radius: 0;
        .vjs-progress-control {
          top: auto;
          bottom: 16px;
          width: calc(100% - 16px * 2);
          .vjs-progress-holder {
            height: 0.3em !important;
          }
        }
      }
    }
    &.vjs-layout-x-small {
      .vjs-custom-rewind-button,
      .vjs-custom-forward-button,
      .theo-button-tooltip,
      .vjs-time-tooltip,
      .vjs-custom-overlay-play-icon,
      .vjs-custom-overlay-pause-icon {
        display: none;
      }
      .vjs-play-control {
        display: none !important;
      }
      .vjs-custom-theater-mode-button {
        display: none !important;
      }
      .vjs-control-bar {
        bottom: 0;
        width: 100%;
        padding: 16px 16px 32px;
        backdrop-filter: blur(0px);
        background: linear-gradient(
          0deg,
          rgba(0, 0, 0, 0.6) 0%,
          rgba(255, 255, 255, 0) 100%
        );
        border-radius: 0;
        .vjs-progress-control {
          top: auto;
          bottom: 16px;
          width: calc(100% - 16px * 2);
        }
      }
      .vjs-custom-mobile-controller .vjs-custom-mobile-controller-button {
        width: 44px;
        height: 44px;
      }
    }

    /**
     * @description
     * スマートフォンのフルスクリーン時のコントロールバーのスタイルを上書きする
     * スマートフォンの時は、ブラウザのデフォルトのCSSが呼ばれるはずなので、PCでスマホサイズにしてフルスクリーンにしない限りは適用されない。
     * ただし、念のためにここでスタイルを上書きしておく。
     */
    &.vjs-fullscreen.vjs-layout-large {
      .vjs-control-bar {
        height: auto !important;
        padding: 32px 16px 8px !important;
        gap: 6px;
      }
      .vjs-progress-control {
        width: auto !important;
      }
      .vjs-custom-fullscreen-comment-button {
        display: none !important;
      }
      .vjs-control:before {
        line-height: 1 !important;
      }
      .theo-live-control {
        font-size: 12px;
      }
      .vjs-remaining-time {
        font-size: 14px;
      }
    }
    &.vjs-fullscreen.vjs-layout-small {
      .vjs-control-bar {
        height: auto !important;
        padding: 16px 16px 32px !important;
      }
      .vjs-progress-control {
        bottom: 24px !important;
        width: auto !important;
      }
      .vjs-custom-fullscreen-comment-button {
        display: none !important;
      }
      .vjs-control:before {
        line-height: 1 !important;
      }
      .theo-live-control {
        font-size: 12px;
      }
      .vjs-remaining-time {
        font-size: 14px;
      }
    }
    &.theo-mobile.vjs-layout-small {
      .vjs-mute-control {
        display: block;
      }
    }
  }

  /* 上記のvideojsに設定されているスタイルを継承しつつ、IVSプレイヤーのスタイルを作成する。 */
  #aws-video-player {
    .vjs-picture-in-picture-control {
      display: none !important;
    }
    /* IVSプレイヤーの時、シークバーが無い分余白を詰めている */
    .vjs-control-bar {
      padding: 8px 16px;
    }
    .vjs-button {
      width: 30px;
      height: 28px;
      cursor: pointer;
    }
    .vjs-volume-panel {
      display: flex;
      align-items: center;
      justify-content: center;
      width: fit-content;
      padding-right: 4px;
      .vjs-volume-tooltip {
        display: none;
      }
    }
    .vjs-live-control {
      padding-left: 4px;
      .vjs-live-display {
        display: flex;
        align-items: center;
        justify-content: center;
        width: fit-content;
        &::before {
          content: "";
          display: inline-block;
          width: 8px;
          height: 8px;
          margin-right: 4px;
          background-color: red;
          border-radius: 50%;
        }
      }
    }
    .vjs-custom-theater-mode-button,
    .vjs-custom-fullscreen-button,
    .vjs-custom-fullscreen-comment-button {
      position: relative;
      img {
        position: absolute;
        inset: 0;
        place-self: center;
        width: 28px;
        height: auto;
      }
    }
    &.vjs-device-android,
    &.vjs-device-iphone {
      /* オーバーレイ表示時のコントロールバーのスタイルを上書きする */
      /* 再生中 or 一時停止中でプレイヤーにフォーカスされている時、コントロールバーが表示される */
      &.vjs-has-started.vjs-playing.vjs-user-active,
      &.vjs-has-started.vjs-paused.vjs-user-active {
        .vjs-cast-menu,
        .vjs-custom-mobile-controller {
          display: flex;
          opacity: 1;
          visibility: visible;
          transition:
            visibility 0.1s,
            opacity 0.1s;
        }
      }
      /* オーバーレイ表示時のコントロールバーのスタイルを上書きする */
      /* 再生中 or 一時停止中でプレイヤーにフォーカスされていない時、コントロールバーが表示されない */
      &.vjs-has-started.vjs-playing.vjs-user-inactive,
      &.vjs-has-started.vjs-paused.vjs-user-inactive {
        .vjs-cast-menu,
        .vjs-custom-mobile-controller {
          display: none;
          opacity: 0;
          visibility: hidden;
          transition:
            visibility 0.1s,
            opacity 0.1s;
        }
      }
    }
    &.vjs-device-iphone.vjs-layout-small,
    &.vjs-device-android.vjs-layout-small {
      .vjs-volume-control {
        display: none;
      }
      .vjs-mute-control {
        display: block;
      }
    }

    /**
     * @description
     * スマートフォンのフルスクリーン時のコントロールバーのスタイルを上書きする
     * スマートフォンの時は、ブラウザのデフォルトのCSSが呼ばれるはずなので、PCでスマホサイズにしてフルスクリーンにしない限りは適用されない。
     * ただし、念のためにここでスタイルを上書きしておく。
     */
    &.fullscreen-mode-enabled.vjs-layout-large,
    &.fullscreen-mode-enabled.vjs-layout-medium,
    &.fullscreen-mode-enabled.vjs-layout-small {
      .vjs-custom-theater-mode-button {
        display: none !important;
      }
      &.vjs-device-iphone,
      &.vjs-device-android {
        .vjs-custom-fullscreen-comment-button {
          display: none !important;
        }
      }
    }
  }
`;

const unmuteButtonStyle = css`
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  align-items: center;
  padding: 7px 14px 7px 12px;
  color: #fff;
  font-size: 12px;
  background-color: rgba(0, 0, 0, 0.7);
  border: 1px solid #fff;
  border-radius: 3px;
  z-index: 20;
  cursor: pointer;
  svg {
    width: 20px;
    height: 20px;
    margin-right: 8px;
  }
`;

const changeLowLatencyStyle = css`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  padding: 8px 14px 8px 12px;
  color: #fff;
  font-size: 12px;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 3px;
  z-index: 20;
  transition: 0.3s;
  opacity: 1;
  visibility: visible;
  &.hide {
    opacity: 0;
    visibility: hidden;
  }
  svg {
    width: 20px;
    height: 20px;
    margin-right: 8px;
  }
`;
